/**
 * expressions.ts — (#face) the templated facial-expression system.
 *
 * Pure resolver: (affect × activity × environment × personality) → FaceSpec.
 * The renderer (scene.drawFace) turns a spec into canvas marks; this module owns
 * only the DECISION, so it stays unit-testable (npx tsx expressions_test.mts).
 *
 * Precedence (highest wins per face part):
 *   instrument (on stage) > activity override > environment fill > affect base.
 * Environment only claims parts the activity left alone, and STACKS accents.
 * Deterministic per ctx — per-pet variety comes from id hashes, never Math.random.
 */
import { hash32 } from './homelayout';

export type Eyes = 'dots' | 'wide' | 'halflid' | 'droopy' | 'arcs' | 'sparkle' | 'squint' | 'focus'
    | 'hearts' | 'spiral' | 'star' | 'wince' | 'up' | 'led';
export type Brows = 'none' | 'raised' | 'knit' | 'flat' | 'sadout';
export type Mouth = 'none' | 'smile' | 'grin' | 'flat' | 'frown' | 'wavy' | 'o' | 'chew' | 'sing' | 'talk' | 'tongue' | 'grit'
    | 'smirk' | 'cat3' | 'gasp';
export type Accent = 'sweat' | 'blush' | 'gloom' | 'sparkles' | 'shiver' | 'steam' | 'heart' | 'note'
    | 'bang' | 'quest' | 'vein' | 'tear' | 'sigh';

export interface FaceSpec { eyes: Eyes; brows: Brows; mouth: Mouth; accents: Accent[]; amp: number; }

export interface FaceCtx {
    id: number; affect: number; moodV: number;
    activity: string; instrument: string;            // instrument ≠ '' → performing on stage
    exposed: boolean; hasUmbrella: boolean;          // exposed = outdoors, not riding/indoors
    weather: string; tempF: number; windMph: number; festival: boolean;
    species: number;                                 // app[0] — species index (P5 skins)
}

// ── (#face P5) species skins — identity flavor, applied ONLY to neutral parts so every
// expressive state (wince, star-eyes, chew…) still reads the same across species ──
export const SPECIES_SKIN: Record<number, { eyes?: Eyes; idleMouth?: Mouth; ampMul?: number }> = {
    1: { eyes: 'led' }, 4: { eyes: 'led' }, 7: { eyes: 'led' },   // mecha-shiba / fox-droid / tanuki-bot — glowing segment eyes
    5: { eyes: 'halflid', ampMul: 0.8 },                          // capybara-mech — unbotherable
    8: { idleMouth: 'wavy', ampMul: 1.05 },                       // jelly-sprite — wobbly resting mouth
    11: { ampMul: 1.1 },                                          // lantern-moth — emotes a touch bigger
    12: { ampMul: 0.75 },                                         // pebble-tortoise — stoic
    14: { idleMouth: 'wavy' },                                    // ghost-kit — spectral shimmer-mouth
};

/** Base face per affect code (affect.ts: 0 neutral … 6 excited). */
export const AFFECT_BASE: Record<number, Omit<FaceSpec, 'amp'>> = {
    0: { eyes: 'dots',    brows: 'none',   mouth: 'none',  accents: [] },
    1: { eyes: 'dots',    brows: 'none',   mouth: 'smile', accents: [] },
    2: { eyes: 'halflid', brows: 'none',   mouth: 'smile', accents: [] },
    3: { eyes: 'halflid', brows: 'flat',   mouth: 'flat',  accents: [] },
    4: { eyes: 'droopy',  brows: 'sadout', mouth: 'frown', accents: ['gloom'] },
    5: { eyes: 'dots',    brows: 'knit',   mouth: 'wavy',  accents: ['sweat'] },
    6: { eyes: 'wide',    brows: 'raised', mouth: 'grin',  accents: ['blush'] },
};

type Part = Partial<Omit<FaceSpec, 'amp'>>;

/** Per-activity face overrides — the big catalog. Grouped by vibe; ids must exist in
 *  server catalog.ts (expressions_test cross-checks them against the real file). */
export const ACT: Record<string, Part> = {};
const g = (ids: string, p: Part) => { for (const id of ids.split(' ')) ACT[id] = p; };

// serene self-care / mindfulness — eyes shut, soft smile
g('brew_tea meditate bath bubble_soak foot_soak facemask_session warm_milk_winddown tai_chi_park '
+ 'bird_listen_park spice_rack_sniff hair_fluff_dry skincare_routine morning_stretch weighted_lounge',
  { eyes: 'arcs', brows: 'none', mouth: 'smile' });
// happily eating — chewing, happy closed eyes
g('eat_leftover eat_out eat_tacos ramen_shop share_meal midnight_noodles pancake_stack morning_toast '
+ 'dango_skewers picnic_solo_park',
  { eyes: 'arcs', mouth: 'chew' });
// sipping — little "o" around the straw / cup rim
g('bubble_tea_lab cafe', { mouth: 'o', eyes: 'halflid' });
// singing its heart out
g('shower_sing karaoke_belt karaoke_duet busk jam_session perform_concert',
  { eyes: 'arcs', mouth: 'sing', accents: ['note'] });
// grooving to a beat
g('radio_dance vinyl_living_sway solo_dance_break dance_battle_neon rhythm_game_combo ffr_practice practice_rhythm',
  { eyes: 'arcs', mouth: 'grin', accents: ['note'] });
// fine-motor crafts — tongue-out concentration
g('origami knit pixel_art model_kit_decals pottery_pinch calligraphy_strokes embroider_hoop sticker_sort '
+ 'paint_pin_badges yarn_swatch scrapbook_layout stamp_album press_flowers bonsai_prune watercolor_clouds '
+ 'macro_photo_desk sketch_pet_self lofi_tab_doodle onigiri_faces dumpling_pleat_duo mealprep_bento '
+ 'cookie_decorate practice_autograph sketch_tree_park nail_buffing',
  { eyes: 'focus', brows: 'knit', mouth: 'tongue' });
// technical tinkering — narrowed eyes, set mouth
g('solder build_bot fix_gadget assemble_kit mod_console etch_circuit calibrate_arm_bot build_pen_plotter '
+ 'wire_plant_sensor debug_blinky_code fix_squeaky_toaster whittle_spoon kintsugi_mug organize_screws '
+ 'rewind_music_box tinker_idle_bench solder_firefly_jar print_tiny_duck repair_dropoff bot_co_repair '
+ 'cowork_build key_copy_errand descale_kettle',
  { eyes: 'focus', brows: 'knit', mouth: 'flat' });
// puzzling / studying — same focus, neutral brows
g('jigsaw_edge_hunt shogi_showdown play_connect4 puzzle_corner flashcard_drill study_together matcha_lesson '
+ 'pickle_jars alphabetize_spice_jars make_bed_corners matcha_whisk journal photo_walk',
  { eyes: 'focus', mouth: 'flat' });
// frustration — knitted brows, wobbly mouth
g('crossword_clue_stuck', { eyes: 'squint', brows: 'knit', mouth: 'wavy', accents: ['quest'] });
g('untangle_charger_cables', { eyes: 'squint', brows: 'knit', mouth: 'wavy' });
g('souffle_fail', { eyes: 'wince', brows: 'sadout', mouth: 'wavy', accents: ['gloom'] });
g('boss_rush_rage', { eyes: 'squint', brows: 'knit', mouth: 'grit', accents: ['steam', 'vein'] });
// physical effort — gritted teeth, sweat
g('mochi_pound scrub_ramen_pot couch_hauling morning_jog_park', { eyes: 'squint', mouth: 'grit', accents: ['sweat'] });
// wide-eyed wonder
g('stargaze cloud_watch_park rank_clouds_park moon_pond_park firefly_jar_park wiki_rabbit_hole '
+ 'window_shopping_wishlist thrift_treasure_dig gachapon claw_machine_quest vending_haul await_concert '
+ 'lantern_market_stroll night_market terrarium leaf_collect_park',
  { eyes: 'sparkle', mouth: 'o' });
// open delight
g('see_concert arcade arcade_battle kite_fly_park photo_booth_faces sticker_swap_meet '
+ 'wave_at_train pillow_fort_build nostalgia_cartoons read_manga picture_book_reread',
  { eyes: 'wide', mouth: 'grin', accents: ['sparkles'] });
// warm caretaking — soft smile + a little heart
g('feed_ducks_park feed_strays acorn_squirrel_park befriend_plaza_pigeon pep_talk_houseplant '
+ 'repot_wilting_succulent water_plants name_the_roomba',
  { eyes: 'dots', mouth: 'smile', accents: ['heart'] });
g('cafe_double_date', { eyes: 'arcs', mouth: 'smile', accents: ['heart', 'blush'] });
// chatty — animated talking mouth
g('visit_chat backfence_gossip narrate_own_life trade', { mouth: 'talk' });
// mischief — the asymmetric smirk
g('produce_haggle invent_secret_handshake', { eyes: 'halflid', mouth: 'smirk' });
g('rehearse_speech_general', { brows: 'knit', mouth: 'talk' });
g('knock', { brows: 'raised', mouth: 'o' });
// spooked
g('scary_movie_peek', { eyes: 'wide', brows: 'raised', mouth: 'wavy', accents: ['sweat'] });
// flat-mouth chores
g('laundry tidy dust_bookshelf_figurines polish_bath_mirror declutter_sock_drawer wipe_workbench '
+ 'sweep_genkan take_out_recycling', { mouth: 'flat' });
// content chores / cozy browsing
g('fluff_couch_cushions bake bake_cookies_with cook_ramen bakery_pickup grocery_run walk_together',
  { eyes: 'dots', mouth: 'smile' });
g('sort_socks_by_mood couch_melt marathon_cozy_series bookstore general_browse riding',
  { eyes: 'halflid', mouth: 'smile' });
g('doomscroll', { eyes: 'halflid', mouth: 'frown', accents: ['gloom'] });
g('mirror_staring_contest', { eyes: 'wide', brows: 'raised', mouth: 'flat' });
g('build_blanket_nest', { eyes: 'focus', mouth: 'tongue' });
g('stationery_splurge', { eyes: 'sparkle', mouth: 'grin' });
g('burrito_sleep sunbeam_catnap', { eyes: 'arcs', mouth: 'smile' });

// ── (#face P2/P4) transient reaction overlay — momentary states that beat the steady-state face ──
export interface OverlayCtx {
    c4: 'turn' | 'wait' | 'won' | 'lost' | null;   // live Connect 4 involvement
    beacon: string;                                 // drama beacon kind on this pet ('' = none)
    startle?: boolean;                              // (P4) thunder crack just hit
    awe?: boolean;                                  // (P4) sky event overhead (window pre-decided by renderer)
    groggy?: boolean;                               // (P4) freshly woken (renderer gates to plain-dot faces)
    moonMisch?: boolean;                            // (P4) full-moon :3 (renderer gates to idle mouths)
}

/** Map transient states to face-part overrides.
 *  Priority: startle > game outcome > sky awe > drama > game flow > moon mischief > grogginess.
 *  Returns null when nothing applies (caller keeps the cached steady-state spec untouched). */
export function overlayFor(o: OverlayCtx): Part | null {
    if (o.startle) return { eyes: 'wide', brows: 'raised', mouth: 'gasp', accents: ['bang'] };
    if (o.c4 === 'won') return { eyes: 'star', brows: 'raised', mouth: 'grin', accents: ['sparkles'] };
    if (o.c4 === 'lost') return { eyes: 'spiral', brows: 'sadout', mouth: 'wavy', accents: ['gloom'] };
    if (o.awe) return { eyes: 'star', brows: 'raised', mouth: 'gasp' };
    switch (o.beacon) {
        case 'romance': return { eyes: 'hearts', mouth: 'smile', accents: ['heart'] };
        case 'crush': return { mouth: 'smile', accents: ['blush'] };          // + shy glances, renderer-side
        case 'rivalry': return { eyes: 'squint', brows: 'knit', mouth: 'flat', accents: ['vein'] };
        case 'reconcile': return { eyes: 'arcs', mouth: 'smile', accents: ['sigh'] };
    }
    if (o.c4 === 'turn') return { eyes: 'up', brows: 'knit', mouth: 'flat' };  // pondering the drop
    if (o.c4 === 'wait') return { eyes: 'focus' };                             // watching the opponent
    if (o.moonMisch) return { mouth: 'cat3' };
    if (o.groggy) return { eyes: 'halflid' };
    return null;
}

/** Merge an overlay onto a spec (parts override, accents stack + dedupe). Never mutates. */
export function applyOverlay(spec: FaceSpec, ov: Part): FaceSpec {
    return { ...spec, ...ov, accents: ov.accents ? [...new Set([...spec.accents, ...ov.accents])] : spec.accents, amp: spec.amp };
}

/** (#face P3) which of a chatting pair holds the floor right now. Symmetric (both pets agree),
 *  deterministic, alternates every ~2.6 s with a per-pair phase so the whole street doesn't sync. */
export function talkTurn(a: number, b: number, tMs: number): number {
    const lo = Math.min(a, b), hi = Math.max(a, b);
    const slot = Math.floor(tMs / 2600 + (hash32('tt:' + lo + ':' + hi) % 13) / 13);
    return slot % 2 === 0 ? lo : hi;
}

/** Resolve the face for a pet right now. */
export function faceFor(c: FaceCtx): FaceSpec {
    const base = AFFECT_BASE[c.affect] ?? AFFECT_BASE[0]!;
    const act = ACT[c.activity];
    const spec: FaceSpec = {
        eyes: act?.eyes ?? base.eyes, brows: act?.brows ?? base.brows, mouth: act?.mouth ?? base.mouth,
        accents: [...base.accents, ...(act?.accents ?? [])], amp: 1,
    };
    // on stage with an instrument → performing face, whatever the activity says
    if (c.instrument) { spec.eyes = 'arcs'; spec.mouth = 'sing'; spec.accents.push('note'); }
    // environment fills only unclaimed parts, stacks accents
    if (c.exposed) {
        if (c.tempF <= 30) { spec.accents.push('shiver'); if (!act?.mouth && (spec.mouth === 'none' || spec.mouth === 'flat')) spec.mouth = 'wavy'; }
        if (c.tempF >= 92) { spec.accents.push('sweat'); if (!act?.mouth && !c.instrument) spec.mouth = 'tongue'; }
        if (c.weather === 'storm' && !act?.brows && c.affect !== 6) spec.brows = 'knit';
        if ((c.weather === 'rain' || c.weather === 'storm') && !c.hasUmbrella && c.moodV < 0.2 && !act?.mouth && !c.instrument) spec.mouth = 'frown';
        if (c.weather === 'snow' && hash32('snowjoy:' + c.id) % 2 === 0) {
            if (!act?.eyes) spec.eyes = 'sparkle';
            if (!act?.mouth && !c.instrument) spec.mouth = 'grin';
        }
        if (c.windMph >= 18 && !act?.eyes) spec.eyes = 'squint';
    }
    if (c.festival) { spec.accents.push('sparkles'); if (!act?.mouth && (spec.mouth === 'none' || spec.mouth === 'flat') && !c.instrument) spec.mouth = 'smile'; }
    // deep sadness wells up regardless of what the pet is doing
    if (c.moodV <= -0.55) spec.accents.push('tear');
    // (#face P5) species skin — only neutral parts; expressive states win
    const skin = SPECIES_SKIN[c.species];
    if (skin?.eyes && spec.eyes === 'dots') spec.eyes = skin.eyes;
    if (skin?.idleMouth && spec.mouth === 'none') spec.mouth = skin.idleMouth;
    // personality amplitude: stable per pet, swelling a little with mood strength, scaled by species
    spec.amp = Math.min(1.45, (0.75 + (hash32('amp:' + c.id) % 41) / 100) * (1 + Math.abs(c.moodV) * 0.25) * (skin?.ampMul ?? 1));
    if (spec.accents.length > 1) spec.accents = [...new Set(spec.accents)];
    return spec;
}
